/**
 * Color Constants
 * Centralized color definitions for consistent styling
 */

// Primary application colors
export const PRIMARY_COLORS = {
  primary: '#A1E430',
  primaryLight: '#B8EC5A',
  primaryDark: '#8BC92A',
  secondary: '#8B7FD9',
  secondaryLight: '#A89BE5',
  secondaryDark: '#7568C7',
  accent: '#1890ff',
  accentHover: '#40a9ff',
  brandPurple: '#8C74FF',
};

// Chart-specific colors
export const CHART_COLORS = {
  primary: '#A1E430',
  secondary: '#FF8C42',
  tertiary: '#8B7FD9',
  quaternary: '#FFE100',
  velocity: '#FF4D4D',
  congestion: '#FF852B',
  anomaly: '#A1E430',
  audio: '#FF8C42',
  vibration: '#8B7FD9',
  environment: '#38A3A5',
  flow: '#52c41a',
  travelTime: '#FFE100',
};

// Road selection colors (for multi-road views)
export const ROAD_COLORS = ['#A1E430', '#8B7FD9', '#FF8C42', '#FFE100'];

// Network view color scales
export const NETWORK_VIEW_COLORS = {
  velocita: {
    high: '#52c41a',      // > 40 km/h - Green
    medium: '#fadb14',    // 20-40 km/h - Yellow
    low: '#ff4d4f',       // < 20 km/h - Red
    noData: '#2C7A7B',    // No data - Teal
  },
  flow: {
    high: '#ff4d4f',      // > 800 vehicles - Red (congested)
    medium: '#fadb14',    // 400-800 vehicles - Yellow
    low: '#52c41a',       // < 400 vehicles - Green (free flow)
    noData: '#2C7A7B',
  },
  travelTime: {
    high: '#ff4d4f',      // > 300 seconds - Red (slow)
    medium: '#fadb14',    // 100-300 seconds - Yellow
    low: '#52c41a',       // < 100 seconds - Green (fast)
    noData: '#2C7A7B',
  },
};

// Legend items for network views
export const LEGEND_ITEMS = {
  velocita: [
    { color: '#52c41a', label: '> 40 km/h' },
    { color: '#fadb14', label: '20-40 km/h' },
    { color: '#ff4d4f', label: '< 20 km/h' },
    { color: '#2C7A7B', label: 'Nessun dato' },
  ],
  flow: [
    { color: '#52c41a', label: '< 400 VEH' },
    { color: '#fadb14', label: '400-800 VEH' },
    { color: '#ff4d4f', label: '> 800 VEH' },
    { color: '#2C7A7B', label: 'Nessun dato' },
  ],
  travelTime: [
    { color: '#52c41a', label: '< 100 s' },
    { color: '#fadb14', label: '100-300 s' },
    { color: '#ff4d4f', label: '> 300 s' },
    { color: '#2C7A7B', label: 'Nessun dato' },
  ],
};

// Semantic colors
export const SEMANTIC_COLORS = {
  success: '#52c41a',
  warning: '#faad14',
  danger: '#ff4d4f',
  info: '#1890ff',
};

// Text colors
export const TEXT_COLORS = {
  primary: '#333333',
  secondary: '#666666',
  muted: '#999999',
  disabled: '#bfbfbf',
  inverse: '#ffffff',
};

// Background colors
export const BG_COLORS = {
  primary: '#ffffff',
  secondary: '#fafafa',
  tertiary: '#f5f5f5',
  dark: '#353A0A',
  overlay: 'rgba(0, 0, 0, 0.45)',
};

// Border colors
export const BORDER_COLORS = {
  default: '#e0e0e0',
  light: '#f0f0f0',
  dark: '#d9d9d9',
};

/**
 * Get color for velocity value
 * @param {number} speed - Speed in km/h
 * @returns {string} Color hex code
 */
export const getVelocityColor = (speed) => {
  if (!speed || speed <= 0) return NETWORK_VIEW_COLORS.velocita.noData;
  if (speed > 40) return NETWORK_VIEW_COLORS.velocita.high;
  if (speed >= 20) return NETWORK_VIEW_COLORS.velocita.medium;
  return NETWORK_VIEW_COLORS.velocita.low;
};

/**
 * Get color for flow value
 * @param {number} flow - Flow in vehicles per 15 min
 * @returns {string} Color hex code
 */
export const getFlowColor = (flow) => {
  if (!flow || flow < 0) return NETWORK_VIEW_COLORS.flow.noData;
  if (flow > 800) return NETWORK_VIEW_COLORS.flow.high;
  if (flow >= 400) return NETWORK_VIEW_COLORS.flow.medium;
  return NETWORK_VIEW_COLORS.flow.low;
};

/**
 * Get color for travel time value
 * @param {number} time - Travel time in seconds
 * @returns {string} Color hex code
 */
export const getTravelTimeColor = (time) => {
  if (!time || time <= 0) return NETWORK_VIEW_COLORS.travelTime.noData;
  if (time > 300) return NETWORK_VIEW_COLORS.travelTime.high;
  if (time >= 100) return NETWORK_VIEW_COLORS.travelTime.medium;
  return NETWORK_VIEW_COLORS.travelTime.low;
};

/**
 * Get color for road index (multi-road selection)
 * @param {number} index - Road index (0-based)
 * @returns {string} Color hex code
 */
export const getRoadColor = (index) => {
  return ROAD_COLORS[index % ROAD_COLORS.length];
};
